import React, { useState } from 'react';
import Image from 'gatsby-image';
import styled from 'styled-components';

import StarRating from './StarRating';
import { H3 } from './StyledComponents/TextStyles';
import CustomMDXRenderer from '../components/mdx/CustomMDXRenderer';

import { bpMaxSM, bpMaxLG, bpMaxMD, bpMaxXXS } from '../utils/breakpoints';

const NoteWrapper = styled.article`
	--border-light: rgba(239, 241, 245, 0.2);
	--border-dark: rgba(239, 241, 245, 0.2);

	.note-cover img {
		/* height: 190px; */
		/* max-width: inherit; */
	}

	.note-title {
		line-height: 1;
		color: var(--color-primary);
	}

	.note-rating {
		color: var(--color-gray-700);
		span {
			height: 18px;
			line-height: 18px;
			display: inline-block;
		}
	}

	.review-date {
		font-family: var(--font-heading);
	}

	.note-metadata {
		color: var(--color-gray-700);
	}

	.note-type {
		top: -3px;
		z-index: 1;
		right: -7px;
		opacity: 0.1;
		font-size: 8rem;
		line-height: 6rem;
		color: var(--color-gray-200);
		font-family: var(--font-heading);
		font-weight: var(--font-weight-bold);
		transition: var(--default-transition);
	}

	.note-type.tv {
		right: 0;
	}

	.note-type.music {
		right: -4px;
	}

	.note-review.excerpt:hover {
		cursor: pointer;
		background-color: var(--color-gray-100);
		transition: background-color 0.2s ease-in-out;
	}

	.note-review.excerpt p {
		display: none;
	}

	.note-review.excerpt > p:first-child {
		overflow: hidden;
		margin-bottom: 0;
		display: -webkit-box;
		-webkit-line-clamp: 6;
		-webkit-box-orient: vertical;
	}

	.note-review.full-note p {
		display: block;
	}

	${bpMaxLG} {
		.note-cover {
			min-width: 160px;
		}
	}

	${bpMaxMD} {
		.note-cover {
			min-width: 140px;
		}
	}

	${bpMaxSM} {
		.note-cover {
			min-width: 120px;
		}

		.note-type {
			top: -2px;
			right: -3px;
			font-size: 4rem;
			line-height: 3rem;
		}

		.note-type.tv {
			right: 1px;
		}

		.note-type.music {
			right: -2px;
		}
	}

	${bpMaxXXS} {
		.note-cover {
			min-width: 105px;
		}
	}

	@media only screen and (min-width: 768px) {
		.note-rating {
			span {
				height: 21px;
				line-height: 21px;
				display: inline-block;
			}
		}

		/* .note-review p {
			line-height: 24px;
		} */
	}

	/* @media only screen and (min-width: 1024px) {
		.note-review p {
			line-height: 26px;
		}
	} */
`;

const noteLabelMap = {
	movie: 'Movie',
	tv: 'TV',
	music: 'Music',
};

const NotePreview = ({ note, isLastNote }) => {
	const [showFullDescription, enableFullDescription] = useState(false);

	const {
		noteType,
		title,
		rating,
		cover,
		author,
		year,
		date,
	} = note.node.frontmatter;

	const authorPrefix =
		noteType === 'tv'
			? 'Created By '
			: noteType === 'movie'
			? 'Directed By '
			: '';

	const wrapperClass = isLastNote
		? 'clear-both relative md:flex py-4 md:py-8 border-t-1 border-gray-100'
		: 'clear-both relative md:flex py-4 md:py-8 border-t-1 border-gray-100';

	const noteReviewClass = showFullDescription ? 'full-note' : 'excerpt';

	const toggleNoteDescription = () => {
		if (!showFullDescription) {
			enableFullDescription(true);
		}
	};

	const reviewDate = new Date(date);
	const currDate = new Date();
	const yearDiff = currDate.getFullYear() - reviewDate.getFullYear();
	let monthDiff = currDate.getMonth() - reviewDate.getMonth();

	monthDiff += yearDiff ? 12 : 0;

	let reviewDateLabel;

	if (monthDiff > 5) {
		reviewDateLabel = (
			<time
				dateTime={new Date(date)}
				className="text-xs sm:text-sm review-date"
			>
				{date}
			</time>
		);
	} else {
		const label =
			monthDiff === 0
				? 'This Month'
				: monthDiff === 1
				? 'Last Month'
				: `${monthDiff} Months Ago`;

		reviewDateLabel = (
			<strong className="uppercase text-xs sm:text-sm review-date">
				{label}
			</strong>
		);
	}

	return (
		<NoteWrapper className={wrapperClass}>
			<div className={`note-type ${noteType} absolute z-0 items-center`}>
				<span className="uppercase inline-block">{noteLabelMap[noteType]}</span>
			</div>
			<div className="note-cover relative float-left md:float-none md:w-1/4 pr-4 pb-2">
				{cover.childImageSharp.fluid ? (
					<Image
						alt={title}
						className="rounded shadow-xl"
						fluid={cover.childImageSharp.fluid}
					/>
				) : (
					<img
						alt={title}
						className="rounded shadow-xl"
						src={cover.childImageSharp.fluid.src}
					/>
				)}
			</div>
			<section className="note-review-content relative z-10 sm:px-2 md:w-3/4">
				<header>
					<H3 customClass="note-title heading pb-1 default-transition">
						{title}
					</H3>
				</header>
				<div className="flex items-center note-rating">
					<StarRating rating={rating} />
					<span className="mx-1 sm:mx-2">·</span>
					{reviewDateLabel}
				</div>
				<div className="note-metadata text-xs sm:text-sm">
					<span className="year number">{year}</span>
					{/* <span className="seperator inline-block mx-1">|</span> */}
					{' | '}
					<span className="author">
						{authorPrefix} {author}
					</span>
				</div>
				<div
					onClick={toggleNoteDescription}
					className={`note-review ${noteReviewClass} rounded text-xs md:text-sm lg:text-base lg:py-1`}
				>
					<CustomMDXRenderer>{note.node.body}</CustomMDXRenderer>
				</div>
			</section>
		</NoteWrapper>
	);
};

export default NotePreview;
